import type {
  Account,
  Article,
  ArticleImage,
  ArticleStatus,
  ArticleVersion,
  AuditLogEntry,
  IntelligenceReport,
  IntelligenceReportVersion,
  Pillar,
  PillarLifecycleStatus,
  PillarResearchStatus,
  Plan,
  ProfileUser,
  Subscription,
  UsageSummary,
  User,
  UserInvitation,
  UserRole,
  WhiteLabelProfile,
  Workspace,
  WorkspaceMembership,
  WorkspaceMembershipRole,
  WordPressTestResult,
  CustomHostnameInfo,
} from '@rankhelm/shared';

const API_BASE = import.meta.env.VITE_API_URL || '';

// In-memory token storage
let authToken: string | null = null;
let refreshToken: string | null = null;

export function setAuthToken(token: string | null) {
  authToken = token;
  if (token) {
    sessionStorage.setItem('rankhelm_token', token);
  } else {
    sessionStorage.removeItem('rankhelm_token');
  }
}

export function setRefreshToken(token: string | null) {
  refreshToken = token;
  if (token) {
    sessionStorage.setItem('rankhelm_refresh_token', token);
  } else {
    sessionStorage.removeItem('rankhelm_refresh_token');
  }
}

export function getAuthToken(): string | null {
  if (authToken) return authToken;
  // Restore from sessionStorage on page refresh
  const stored = sessionStorage.getItem('rankhelm_token');
  if (stored) {
    authToken = stored;
  }
  return authToken;
}

export function getRefreshToken(): string | null {
  if (refreshToken) return refreshToken;
  const stored = sessionStorage.getItem('rankhelm_refresh_token');
  if (stored) {
    refreshToken = stored;
  }
  return refreshToken;
}

interface ApiOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

async function request<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const { body, ...rest } = options;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(rest.headers as Record<string, string>),
  };

  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...rest,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  const text = await res.text();
  let json: unknown;
  try {
    json = text ? JSON.parse(text) : {};
  } catch {
    throw new ApiError('Invalid response from server', res.status);
  }

  if (!res.ok) {
    const errBody = json as { error?: string };
    throw new ApiError(errBody.error || 'Request failed', res.status);
  }

  return json as T;
}

export class ApiError extends Error {
  constructor(message: string, public status: number) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Thrown when login succeeds but MFA verification is required before access is granted. */
export class MfaRequiredError extends Error {
  constructor(public mfaToken: string) {
    super('MFA verification required');
    this.name = 'MfaRequiredError';
  }
}

// --- Auth response types ---
interface AuthPayload {
  token: string;
  refresh_token: string;
  user: User;
  account: Account;
}

interface LoginPayload {
  token?: string;
  refresh_token?: string;
  user?: User;
  account?: Account;
  mfa_required?: boolean;
  mfa_token?: string;
}

// --- Auth ---
export const auth = {
  signup: (data: { email: string; password: string; name: string; account_name: string }) =>
    request<{ success: boolean; data: AuthPayload }>('/api/auth/signup', { method: 'POST', body: data }),
  login: (data: { email: string; password: string }) =>
    request<{ success: boolean; data: LoginPayload }>('/api/auth/login', { method: 'POST', body: data }),
  mfaChallenge: (data: { mfa_token: string; code: string }) =>
    request<{ success: boolean; data: AuthPayload }>('/api/auth/mfa/challenge', { method: 'POST', body: data }),
  logout: () => {
    const rt = getRefreshToken();
    return request<{ success: boolean }>('/api/auth/logout', { method: 'POST', body: rt ? { refresh_token: rt } : undefined });
  },
  me: () => request<{ success: boolean; data: { user: User; account: Account } }>('/api/auth/me'),
  requestMagicLink: (data: { email: string }) =>
    request<{ success: boolean; message: string }>('/api/auth/magic-link', { method: 'POST', body: data }),
  verifyMagicLink: (data: { token: string; email: string }) =>
    request<{ success: boolean; data: LoginPayload }>('/api/auth/magic-link/verify', { method: 'POST', body: data }),
  impersonate: (userId: string) =>
    request<{ success: boolean; data: AuthPayload & { impersonator_id: string } }>(
      `/api/auth/impersonate/${userId}`,
      { method: 'POST' },
    ),
  exitImpersonation: () => {
    const rt = getRefreshToken();
    return request<{ success: boolean; data: AuthPayload }>('/api/auth/impersonate/exit', {
      method: 'POST',
      body: rt ? { refresh_token: rt } : undefined,
    });
  },
};

// --- Accounts (formerly Organizations) ---

export interface AccountUpdateResponse {
  success: boolean;
  data: Account;
  /** Present immediately after `custom_domain` changes; null otherwise. */
  hostname: CustomHostnameInfo | null;
  /** Populated when the Cloudflare API call failed but the DB write succeeded. */
  cf_error?: string;
}

export interface CustomDomainRefreshResponse {
  success: boolean;
  data: Account;
  hostname: CustomHostnameInfo | null;
}

export const accountsApi = {
  list: () =>
    request<{ success: boolean; data: Account[] }>('/api/accounts'),
  get: (id: string) =>
    request<{ success: boolean; data: Account }>(`/api/accounts/${id}`),
  listUsers: (id: string) =>
    request<{ success: boolean; data: User[] }>(`/api/accounts/${id}/users`),
  listWorkspaces: (id: string) =>
    request<{ success: boolean; data: Workspace[] }>(`/api/accounts/${id}/workspaces`),
  create: (data: { name: string; type?: string }) =>
    request<{ success: boolean; data: Account }>('/api/accounts', { method: 'POST', body: data }),
  update: (id: string, data: Partial<Pick<Account, 'name' | 'slug' | 'custom_domain'>>) =>
    request<AccountUpdateResponse>(`/api/accounts/${id}`, { method: 'PATCH', body: data }),
  /**
   * Re-poll Cloudflare for the current hostname state and refresh the
   * denormalized status column. Frontend calls this while the domain is
   * pending to watch ownership + SSL flip to active.
   */
  refreshCustomDomain: (id: string) =>
    request<CustomDomainRefreshResponse>(`/api/accounts/${id}/custom-domain/refresh`, {
      method: 'POST',
    }),
};

// --- Workspaces (replaces Clients + Projects) ---
export const workspacesApi = {
  list: () =>
    request<{ success: boolean; data: Workspace[] }>('/api/workspaces'),
  get: (id: string) =>
    request<{ success: boolean; data: Workspace }>(`/api/workspaces/${id}`),
  create: (data: Record<string, unknown>) =>
    request<{ success: boolean; data: Workspace }>('/api/workspaces', { method: 'POST', body: data }),
  update: (id: string, data: Record<string, unknown>) =>
    request<{ success: boolean }>(`/api/workspaces/${id}`, { method: 'PATCH', body: data }),
  /**
   * Fetch (or synchronously render) the workspace's website screenshot.
   * Backed by Cloudflare Browser Rendering + R2 (ADR-023). First call per
   * (workspace × URL × week) can take 3–10 s; cached hits return instantly.
   */
  screenshot: (id: string) =>
    request<{
      success: boolean;
      data: {
        url: string;
        key: string;
        generated_at: string;
        cached: boolean;
      };
    }>(`/api/workspaces/${id}/screenshot`),
};

// --- Business Context Research (Perplexity + Claude pipeline) ---
export interface BusinessContextDraft {
  businessReach: 'Local' | 'Regional' | 'National' | 'Global' | '';
  businessType: 'B2B' | 'B2C' | 'Both' | '';
  serviceArea: string;
  primaryServices: string[];
  idealCustomerProfile: string;
  knownCompetitors: { name: string; url: string }[];
  marketingChannels: string[];
  contentGoals: string[];
  whatMakesYouDifferent: string;
  additionalContext: string;
}
export type BusinessContextResearchStatus =
  | 'pending' | 'researching' | 'normalizing' | 'completed' | 'failed';
export interface BusinessContextResearchJob {
  id: string;
  status: BusinessContextResearchStatus;
  progress_message: string | null;
  result: BusinessContextDraft | null;
  sources: string[];
  error: string | null;
  created_at: string;
  updated_at: string;
}
export const businessContextResearchApi = {
  latest: (workspaceId: string) =>
    request<{ success: boolean; data: BusinessContextResearchJob | null }>(
      `/api/workspaces/${workspaceId}/business-context/research`,
    ),
  start: (workspaceId: string) =>
    request<{ success: boolean; data: { jobId: string; status: BusinessContextResearchStatus } }>(
      `/api/workspaces/${workspaceId}/business-context/research`,
      { method: 'POST', body: {} },
    ),
  get: (workspaceId: string, jobId: string) =>
    request<{ success: boolean; data: BusinessContextResearchJob }>(
      `/api/workspaces/${workspaceId}/business-context/research/${jobId}`,
    ),
};

// --- Workspace Memberships (replaces Editor Assignments) ---
export const workspaceMembershipsApi = {
  list: (workspaceId: string) =>
    request<{ success: boolean; data: WorkspaceMembership[] }>(`/api/workspace-memberships?workspace_id=${workspaceId}`),
  create: (data: { user_id: string; workspace_id: string; role?: WorkspaceMembershipRole }) =>
    request<{ success: boolean }>('/api/workspace-memberships', { method: 'POST', body: data }),
  update: (id: string, data: { role: WorkspaceMembershipRole }) =>
    request<{ success: boolean }>(`/api/workspace-memberships/${id}`, { method: 'PATCH', body: data }),
  remove: (id: string) =>
    request<{ success: boolean }>(`/api/workspace-memberships/${id}`, { method: 'DELETE' }),
};

// --- Audit ---
export const auditApi = {
  list: (limit?: number) =>
    request<{ success: boolean; data: AuditLogEntry[] }>(`/api/audit?limit=${limit || 50}`),
};

// --- Pillars ---
export const pillarsApi = {
  list: (workspaceId: string) =>
    request<{ success: boolean; data: Pillar[] }>(`/api/pillars?workspace_id=${workspaceId}`),
  get: (id: string) =>
    request<{ success: boolean; data: Pillar }>(`/api/pillars/${id}`),
  create: (data: { workspace_id: string; name: string; description?: string }) =>
    request<{ success: boolean; data: Pillar }>('/api/pillars', { method: 'POST', body: data }),
  update: (id: string, data: Partial<Pick<Pillar, 'name' | 'description' | 'sort_order'> & { research_status: PillarResearchStatus; lifecycle_status: PillarLifecycleStatus }>) =>
    request<{ success: boolean }>(`/api/pillars/${id}`, { method: 'PATCH', body: data }),
  delete: (id: string) =>
    request<{ success: boolean }>(`/api/pillars/${id}`, { method: 'DELETE' }),
  generateTopics: (id: string) =>
    request<{ success: boolean; message: string }>(`/api/pillars/${id}/generate-topics`, { method: 'POST' }),
};

// --- Articles ---
export const articlesApi = {
  list: (workspaceId: string, filters?: { status?: ArticleStatus; pillar_id?: string }) =>
    request<{ success: boolean; data: Article[] }>(
      `/api/articles?workspace_id=${workspaceId}${filters?.status ? `&status=${filters.status}` : ''}${filters?.pillar_id ? `&pillar_id=${filters.pillar_id}` : ''}`
    ),
  get: (id: string) =>
    request<{ success: boolean; data: Article }>(`/api/articles/${id}`),
  create: (data: { workspace_id: string; pillar_id?: string; title: string; target_keyword?: string }) =>
    request<{ success: boolean; data: Article }>('/api/articles', { method: 'POST', body: data }),
  update: (id: string, data: Partial<Pick<Article, 'title' | 'slug' | 'status' | 'content_json' | 'seo_meta_json' | 'target_keyword' | 'scheduled_at'>>) =>
    request<{ success: boolean }>(`/api/articles/${id}`, { method: 'PATCH', body: data }),
  delete: (id: string) =>
    request<{ success: boolean }>(`/api/articles/${id}`, { method: 'DELETE' }),
  versions: (id: string) =>
    request<{ success: boolean; data: ArticleVersion[] }>(`/api/articles/${id}/versions`),
  generate: (id: string) =>
    request<{ success: boolean; message: string; data: Article; similar_articles?: Article[] }>(
      `/api/articles/${id}/generate`, { method: 'POST' }
    ),
  bulkGenerate: (workspaceId: string, articleIds: string[]) =>
    request<{ success: boolean; message: string; data: Article[] }>(
      `/api/articles/bulk-generate`, { method: 'POST', body: { workspace_id: workspaceId, article_ids: articleIds } }
    ),
  images: (id: string) =>
    request<{ success: boolean; data: ArticleImage[] }>(`/api/articles/${id}/images`),
  selectImage: (id: string, imageId: string) =>
    request<{ success: boolean }>(`/api/articles/${id}/select-image`, { method: 'POST', body: { image_id: imageId } }),
  publish: (id: string) =>
    request<{ success: boolean; message: string; data: Article }>(
      `/api/articles/${id}/publish`, { method: 'POST' }
    ),
};

// --- Users/Team ---
export const usersApi = {
  list: () =>
    request<{ success: boolean; data: User[] }>('/api/users'),
  updateRole: (id: string, role: UserRole) =>
    request<{ success: boolean }>(`/api/users/${id}/role`, { method: 'PATCH', body: { role } }),
  invite: (data: { email: string; first_name: string; last_name: string; role: string; account_id?: string }) =>
    request<{ success: boolean }>('/api/users/invite', { method: 'POST', body: data }),
  listInvitations: () =>
    request<{ success: boolean; data: UserInvitation[] }>('/api/users/invitations'),
  revokeInvitation: (id: string) =>
    request<{ success: boolean }>(`/api/users/invitations/${id}`, { method: 'DELETE' }),
  acceptInvite: (data: { token: string; password: string; name: string }) =>
    request<{ success: boolean; data: AuthPayload }>(
      '/api/users/accept-invite', { method: 'POST', body: data }
    ),
  getInviteInfo: (token: string) =>
    request<{ success: boolean; data: { first_name: string | null; last_name: string | null; email: string; role: string; account_name: string | null } }>(
      `/api/users/invite-info?token=${token}`
    ),
};

// --- WordPress ---
export const wordpressApi = {
  testConnection: (data: { url: string; app_password: string; username?: string }) =>
    request<{ success: boolean; data: WordPressTestResult }>('/api/wordpress/test-connection', { method: 'POST', body: data }),
  testWorkspace: (workspaceId: string) =>
    request<{ success: boolean; data: WordPressTestResult }>(`/api/wordpress/test-workspace/${workspaceId}`, { method: 'POST' }),
};

// --- Intelligence Reports ---
export const intelligenceApi = {
  get: (workspaceId: string) =>
    request<{ success: boolean; data: IntelligenceReport & { current_version?: IntelligenceReportVersion } }>(`/api/intelligence?workspace_id=${workspaceId}`),
  generate: (workspaceId: string) =>
    request<{ success: boolean; message: string }>('/api/intelligence/generate', { method: 'POST', body: { workspace_id: workspaceId } }),
  feedback: (id: string, feedbackPrompt: string) =>
    request<{ success: boolean; message: string }>(`/api/intelligence/${id}/feedback`, { method: 'POST', body: { feedback_prompt: feedbackPrompt } }),
  update: (id: string, data: { status?: string }) =>
    request<{ success: boolean }>(`/api/intelligence/${id}`, { method: 'PATCH', body: data }),
  regenerate: (id: string) =>
    request<{ success: boolean; message: string }>(`/api/intelligence/${id}/regenerate`, { method: 'POST' }),
  getVersion: (reportId: string, versionId: string) =>
    request<{ success: boolean; data: IntelligenceReportVersion }>(`/api/intelligence/${reportId}/version/${versionId}`),
};

// --- Billing ---
export const billingApi = {
  plans: () =>
    request<{ success: boolean; data: Plan[] }>('/api/billing/plans'),
  subscription: () =>
    request<{ success: boolean; data: Subscription }>('/api/billing/subscription'),
  usage: () =>
    request<{ success: boolean; data: UsageSummary }>('/api/billing/usage'),
  createCheckout: (data: { plan_id: string; success_url: string; cancel_url: string }) =>
    request<{ success: boolean; data: { url: string } }>('/api/billing/checkout', { method: 'POST', body: data }),
  createPortal: (data: { return_url: string }) =>
    request<{ success: boolean; data: { url: string } }>('/api/billing/portal', { method: 'POST', body: data }),
};

// --- White-Label ---
async function uploadBrandingFile(
  accountId: string,
  kind: 'logo' | 'favicon',
  file: File,
): Promise<{ url: string; profile: WhiteLabelProfile }> {
  const form = new FormData();
  form.append('file', file);
  const headers: Record<string, string> = {};
  const token = getAuthToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}/api/accounts/${accountId}/branding/${kind}`, {
    method: 'POST',
    headers,
    body: form,
  });
  const text = await res.text();
  let json: { success?: boolean; data?: { url: string; profile: WhiteLabelProfile }; error?: string } = {};
  try {
    json = text ? JSON.parse(text) : {};
  } catch {
    throw new ApiError('Invalid response from server', res.status);
  }
  if (!res.ok || !json.success || !json.data) {
    throw new ApiError(json.error || `${kind} upload failed`, res.status);
  }
  return json.data;
}

export const whiteLabelApi = {
  get: (accountId: string) =>
    request<{ success: boolean; data: WhiteLabelProfile }>(`/api/accounts/${accountId}/white-label`),
  upsert: (accountId: string, data: Partial<Omit<WhiteLabelProfile, 'id' | 'account_id' | 'created_at' | 'updated_at'>>) =>
    request<{ success: boolean; data: WhiteLabelProfile }>(`/api/accounts/${accountId}/white-label`, { method: 'PUT', body: data }),
  uploadLogo: (accountId: string, file: File) => uploadBrandingFile(accountId, 'logo', file),
  uploadFavicon: (accountId: string, file: File) => uploadBrandingFile(accountId, 'favicon', file),
};

// --- Profile ---
export const profileApi = {
  get: () =>
    request<{ success: boolean; data: ProfileUser }>('/api/profile'),
  update: (data: { name?: string; avatar_url?: string | null }) =>
    request<{ success: boolean; data: ProfileUser }>('/api/profile', { method: 'PATCH', body: data }),
  changePassword: (data: { current_password: string; new_password: string }) =>
    request<{ success: boolean; message: string }>('/api/profile/change-password', { method: 'POST', body: data }),
  uploadAvatar: async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    const headers: Record<string, string> = {};
    const token = getAuthToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${API_BASE}/api/profile/avatar`, {
      method: 'POST',
      headers,
      body: form,
    });
    const text = await res.text();
    let json: { success?: boolean; data?: { avatar_url: string; user: ProfileUser }; error?: string } = {};
    try {
      json = text ? JSON.parse(text) : {};
    } catch {
      throw new ApiError('Invalid response from server', res.status);
    }
    if (!res.ok || !json.success || !json.data) {
      throw new ApiError(json.error || 'Avatar upload failed', res.status);
    }
    return json.data;
  },
};
